import { Box, Table as MUITable, TableBody, TableHead, TableRow } from '@material-ui/core';
import React, { ComponentType, memo, useCallback, useEffect, useRef } from 'react';
import {
  ColumnInstance,
  Row,
  TableInstance,
  TableState,
  useBlockLayout,
  useFilters,
  UseFiltersInstanceProps,
  UseFiltersState,
  useGlobalFilter,
  UseGlobalFiltersInstanceProps,
  useRowSelect,
  UseRowSelectInstanceProps,
  useSortBy,
  UseSortByColumnProps,
  UseSortByOptions,
  UseSortByState,
  useTable,
} from 'react-table';
import { FixedSizeList, ListChildComponentProps } from 'react-window';
import { SortArrow } from '../../../assets/img/SortArrow';
import { TableBodyCell, TableContainer, TableHeaderCell, TableSortLabel } from './TableComponents';
import { TableColumn } from './types';
import { NoResultsFound } from './NoResultsFound';
import { FilterProvider } from '../filters/FilterContext';

type SelectedRows = Record<number, boolean>;

export type TableData = Record<string, any>;

interface CustomTable<Data extends TableData>
  extends TableInstance<Data>,
    UseRowSelectInstanceProps<Data>,
    UseGlobalFiltersInstanceProps<Data>,
    UseFiltersInstanceProps<Data> {
  headers: Array<ColumnInstance<Data> & UseSortByColumnProps<Data>>;
  isAllPageRowsSelected: boolean;
  state: TableState<Data> & { selectedRowIds: SelectedRows };
}

const extraSortOptions: UseSortByOptions<any> = {
  disableSortRemove: true,
  autoResetSortBy: false,
  disableMultiSort: true,
};

export type FilterProps<Data extends TableData> = Pick<
  UseFiltersInstanceProps<Data>,
  'setFilter' | 'setAllFilters'
> &
  Pick<UseFiltersState<Data>, 'filters'> &
  Pick<UseGlobalFiltersInstanceProps<Data>, 'setGlobalFilter'>;

export interface TableProps<Data extends TableData> {
  data: Data[];
  columns: Array<TableColumn<Data>>;
  onSelect?: (rows: Row<Data>[]) => void;
  sortBy?: UseSortByState<Data>;
  Filters?: JSX.Element;
}

const boxStyles: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'nowrap',
  alignItems: 'center',
  boxSizing: 'border-box',
  minWidth: '100%',
  width: '100%',
};

export const Table = <Data extends TableData>({
  data,
  columns,
  onSelect,
  sortBy,
  Filters,
}: TableProps<Data>): JSX.Element => {
  const mountRef = useRef(false);
  const {
    rows,
    prepareRow,
    headers,
    selectedFlatRows,
    state,
    getTableBodyProps,
    setGlobalFilter,
    setAllFilters,
    setFilter,
  } = useTable(
    {
      ...extraSortOptions,
      data,
      columns,
      initialState: {
        ...sortBy,
      },
    },
    useGlobalFilter,
    useFilters,
    useSortBy,
    useRowSelect,
    useBlockLayout,
  ) as CustomTable<Data>;

  useEffect(() => {
    if (!mountRef.current) {
      mountRef.current = true;
      return;
    }
    onSelect && onSelect(selectedFlatRows);
  }, [onSelect, selectedFlatRows]);

  const allRowsFiltered = data.length && !rows.length && state.filters.length;
  const getItemKey = useCallback((index: number) => rows[index].id, [rows]);

  const RenderRow: ComponentType<ListChildComponentProps<any>> = ({ index, style }) => {
    const row = rows[index];
    prepareRow(row);

    return (
      <TableRow {...row.getRowProps({ style })} component='div'>
        <Box style={boxStyles}>
          {row.cells.map((cell) => (
            <TableBodyCell {...cell.getCellProps()} component='div' variant='body'>
              {cell.render('Cell')}
            </TableBodyCell>
          ))}
        </Box>
      </TableRow>
    );
  };

  return (
    <>
      {Filters && (
        <FilterProvider
          setGlobalFilter={setGlobalFilter}
          filters={state.filters}
          setFilter={setFilter}
          setAllFilters={setAllFilters}
        >
          <Box marginBottom={2}>{Filters}</Box>
        </FilterProvider>
      )}
      <TableContainer>
        <MUITable aria-label='table' component='div'>
          <TableHead component='div'>
            <TableRow component='div'>
              {headers.map((column) => (
                <TableHeaderCell
                  {...column.getHeaderProps(column.getSortByToggleProps())}
                  component='div'
                >
                  {column.canSort ? (
                    <TableSortLabel
                      active={column.isSorted}
                      direction={column.isSortedDesc ? 'desc' : 'asc'}
                      IconComponent={SortArrow}
                    >
                      {column.render('Header')}
                    </TableSortLabel>
                  ) : (
                    <>{column.render('Header')}</>
                  )}
                </TableHeaderCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody aria-label='table body' component='div' {...getTableBodyProps()}>
            {allRowsFiltered ? <NoResultsFound /> : null}
            <FixedSizeList
              overscanCount={10}
              width={'100%'}
              height={800}
              itemCount={rows.length}
              itemSize={80}
              itemKey={getItemKey}
              children={RenderRow}
            />
          </TableBody>
        </MUITable>
      </TableContainer>
    </>
  );
};

export const PureTable = memo(Table) as typeof Table;
